/**
 * Shared helpers for the Design-tab drawer modules (Colors, Fonts, Layout,
 * Section). Lifted out of property-panel.js so the drawer modules can import
 * them directly without taking a PropertyPanel reference. property-panel.js
 * re-imports each helper and keeps its existing method names as thin
 * delegators so internal callers stay valid during the incremental Phase B
 * lift.
 *
 * Everything here is pure (no DOM mutation, no `this` context) except where
 * explicitly documented — `syncColorSwatches` mutates the classList of
 * swatch buttons inside the passed scope.
 */

import { getCurrentTheme, getThemeVars } from '../../theme/theme-editor.js';
import { TOKEN_PRIORITY } from '../../shared/theme-tokens.js';
import { colorToPickerHex, parseCssColor } from '../../shared/color-utils.js';
import { contrastRatio } from '../../shared/wcag-contrast.js';
export { escapeHTML } from '../../shared/html-escape.js';
export { TOKEN_PRIORITY } from '../../shared/theme-tokens.js';
export { assessContrast, formatBadge } from '../../shared/wcag-contrast.js';

const RECENT_COLORS_KEY = 'pb-recent-theme-colors';
const MAX_RECENT_COLORS = 8;

// ── String / value helpers ─────────────────────────────────────────────────

/**
 * Normalize any parseable CSS color to a picker-safe `#rrggbb` (alpha dropped),
 * or null when unparseable. Delegates to the shared color utility so the parse
 * path is unified with the rest of the editing UI (hex/rgb()/named/color(srgb));
 * preserves the historical null-on-failure contract its callers rely on.
 */
export function normalizeHex(val) {
  return colorToPickerHex(val) || null;
}

/** Resolve a literal or `var(--theme-token)` value to a picker-safe hex. */
export function colorValueToHex(value, theme = getCurrentTheme() || {}) {
  return colorToPickerHex(value, { variables: theme }) || null;
}

export function getRecentColors(storage = globalThis.localStorage) {
  if (!storage) return [];
  try {
    const parsed = JSON.parse(storage.getItem(RECENT_COLORS_KEY) || '[]');
    if (!Array.isArray(parsed)) return [];
    const seen = new Set();
    const out = [];
    for (const value of parsed) {
      const normalized = normalizeHex(value);
      const key = normalized?.toLowerCase();
      if (!normalized || seen.has(key)) continue;
      seen.add(key);
      out.push(normalized);
      if (out.length >= MAX_RECENT_COLORS) break;
    }
    return out;
  } catch {
    return [];
  }
}

export function rememberRecentColor(value, storage = globalThis.localStorage) {
  const normalized = normalizeHex(value);
  if (!normalized || !storage) return getRecentColors(storage);
  const next = [
    normalized,
    ...getRecentColors(storage).filter((item) => item.toLowerCase() !== normalized.toLowerCase())
  ].slice(0, MAX_RECENT_COLORS);
  try {
    storage.setItem(RECENT_COLORS_KEY, JSON.stringify(next));
  } catch {
    /* optional preference */
  }
  return next;
}

/**
 * Historical contrast helpers, now thin shims over shared/wcag-contrast.js
 * (the single WCAG math implementation). Names, signatures, and return
 * shapes are preserved for existing callers; `aaa` is an additive field.
 */
export function getContrastRatio(colorA, colorB) {
  const a = parseCssColor(normalizeHex(colorA) || '');
  const b = parseCssColor(normalizeHex(colorB) || '');
  if (!a || !b) return Number.NaN;
  return contrastRatio(a, b);
}

export function getContrastAssessment(foreground, background) {
  const ratio = getContrastRatio(foreground, background);
  if (!Number.isFinite(ratio)) return null;
  return {
    ratio,
    label: `${ratio.toFixed(1)}:1`,
    aa: ratio >= 4.5,
    aaa: ratio >= 7,
    largeText: ratio >= 3
  };
}

/** Attribute-context escape (does NOT escape apostrophe — quoted with `"`). */
export function escapeAttr(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Extract the first CSS declaration value for `propertyName` from a rule chunk. */
export function matchCSSDeclaration(ruleChunk, propertyName) {
  if (!ruleChunk || !propertyName) return '';
  const escaped = propertyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = ruleChunk.match(new RegExp(`(?:^|[;{])\\s*${escaped}\\s*:\\s*([^;]+)`, 'i'));
  return match ? match[1].trim() : '';
}

export function isDisplayNoneRule(ruleChunk) {
  return /(?:^|[;{])\s*display\s*:\s*none\b/i.test(ruleChunk || '');
}

// ── Theme-colour swatch palette ────────────────────────────────────────────

/**
 * Returns the deduped list of palette swatches drawn from the current theme.
 * Preferred token order is fixed; any extra `--*` vars in the theme are
 * appended after. Hex values are normalized; identical colors are deduped
 * regardless of input casing or shorthand.
 */
export function getDesignColorOptions() {
  const theme = getCurrentTheme() || {};
  const defs = getThemeVars();
  const labelByKey = new Map(defs.map((def) => [def.key, def.label]));
  const preferred = TOKEN_PRIORITY;
  const orderedKeys = [
    ...preferred.filter((key) => key in theme),
    ...Object.keys(theme).filter((key) => key.startsWith('--') && !preferred.includes(key))
  ];

  const options = [];
  const seen = new Set();
  for (const key of orderedKeys) {
    const normalized = normalizeHex(theme[key] || '');
    if (!normalized) continue;
    const dedupeKey = normalized.toLowerCase();
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    options.push({
      key,
      label: labelByKey.get(key) || key.replace(/^--/, ''),
      value: normalized
    });
  }
  return options;
}

// ── Color-input renderers (return HTML strings) ────────────────────────────

/**
 * Render the swatch grid for a colour input. `leadingOptions` lets callers
 * prepend custom entries (e.g. "transparent" / "current") before the theme
 * palette.
 */
export function renderDesignColorSwatches(targetId, selectedValue = '', leadingOptions = []) {
  const selectedRaw = String(selectedValue || '').trim();
  const selected = colorValueToHex(selectedRaw);
  const recentOptions = getRecentColors().map((value) => ({
    label: 'Recent color',
    value,
    recent: true
  }));
  const seen = new Set();
  return [...leadingOptions, ...getDesignColorOptions(), ...recentOptions]
    .filter((opt) => {
      if (opt.clearValue) return true;
      const normalized = normalizeHex(opt.value || '');
      const key = normalized?.toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((opt) => {
      const optHex = normalizeHex(opt.value || '');
      const storedValue = opt.key ? `var(${opt.key})` : optHex || opt.value || '';
      const isTokenMatch = !!opt.key && selectedRaw === storedValue;
      const isActive =
        (!selectedRaw && opt.clearValue) || isTokenMatch || (selected && optHex === selected)
          ? ' pb-active'
          : '';
      const labelClass = opt.visibleLabel ? ' pb-color-swatch-btn--label' : '';
      const label = escapeAttr(`${opt.label} (${optHex || opt.value})`);
      const safeTarget = escapeAttr(targetId);
      const clearAttr = opt.clearValue ? ' data-color-clear="true"' : '';
      const recentAttr = opt.recent ? ' data-color-recent="true"' : '';
      return `<button type="button" class="pb-color-swatch-btn${labelClass}${isActive}" data-color-target="${safeTarget}" data-color-value="${escapeAttr(storedValue)}" data-color-hex="${escapeAttr(optHex || '')}"${clearAttr}${recentAttr} data-tooltip="${label}" aria-label="${label}">
        <span class="pb-color-swatch-dot" style="background:${optHex || opt.value}"></span>
        ${opt.visibleLabel ? `<span class="pb-color-swatch-label">${escapeAttr(opt.label)}</span>` : ''}
      </button>`;
    })
    .join('');
}

/**
 * Render the standard "swatch row + hex input + native color picker" trio
 * used everywhere the Design tab edits a colour token.
 */
export function renderTokenFirstColorInput({
  colorId,
  hexId,
  colorValue,
  hexValue = '',
  placeholder = '',
  colorAriaLabel = 'Color',
  hexAriaLabel = 'Hex color value',
  colorInputClass = '',
  hexInputClass = '',
  wrapClass = '',
  extraColorAttrs = '',
  extraHexAttrs = '',
  leadingSwatches = [],
  swatchSelectedValue = null
}) {
  const normalizedColor = colorValueToHex(colorValue || '') || '#000000';
  const selectedSource =
    swatchSelectedValue === null ? hexValue || colorValue || '' : swatchSelectedValue;
  const selectedValue = selectedSource || (swatchSelectedValue === null ? normalizedColor : '');
  const eyedropperHidden = typeof globalThis.EyeDropper === 'function' ? '' : ' hidden';
  return `
      <div class="pb-color-input-wrap${wrapClass ? ` ${wrapClass}` : ''}">
        <div class="pb-color-swatch-row">
          ${renderDesignColorSwatches(colorId, selectedValue, leadingSwatches)}
        </div>
        <div class="pb-color-fallback-row">
          <input type="text" class="pb-color-hex${hexInputClass ? ` ${hexInputClass}` : ''}" id="${escapeAttr(hexId)}" value="${escapeAttr(hexValue || '')}" placeholder="${escapeAttr(placeholder)}" spellcheck="false" aria-label="${escapeAttr(hexAriaLabel)}" ${extraHexAttrs} />
          <input type="color" class="pb-color-input${colorInputClass ? ` ${colorInputClass}` : ''}" id="${escapeAttr(colorId)}" value="${normalizedColor}" aria-label="${escapeAttr(colorAriaLabel)}" ${extraColorAttrs} />
          <button type="button" class="pb-color-eyedropper" data-color-eyedropper="${escapeAttr(colorId)}" aria-label="Pick color from screen" data-tooltip="Pick color from screen"${eyedropperHidden}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m2 22 1-1h3l9-9"/><path d="M3 21v-3l9-9"/><path d="m15 6 3.4-3.4a2.1 2.1 0 0 1 3 3L18 9"/><path d="m14 5 5 5"/><path d="m5 14 5 5"/></svg>
          </button>
        </div>
      </div>
    `;
}

/**
 * Re-mark the active swatch button inside `scopeEl` for the given target id
 * based on `selectedValue`. Pure DOM mutation — no return value.
 */
export function syncColorSwatches(scopeEl, targetId, selectedValue) {
  if (!scopeEl || !targetId) return;
  const selectedRaw = String(selectedValue || '').trim();
  const selected = colorValueToHex(selectedRaw);
  for (const btn of scopeEl.querySelectorAll('.pb-color-swatch-btn')) {
    if (btn.dataset.colorTarget !== targetId) continue;
    const swatchValue = btn.dataset.colorValue || '';
    const swatchHex = normalizeHex(btn.dataset.colorHex || swatchValue);
    const isClear = btn.dataset.colorClear === 'true';
    btn.classList.toggle(
      'pb-active',
      (!selectedRaw && isClear) ||
        (!!selectedRaw && swatchValue === selectedRaw) ||
        (!!selected && swatchHex === selected)
    );
  }
}
